import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  Suspense,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "@/components/store/Header";
import Footer from "@/components/store/Footer";
import Spinner from "@/components/loader/Spinner";
import DesktopOperationalTimes from "@/components/store/DesktopOperationalTimes";
import "@/assets/css/StoreFront.css";
import { Helmet } from "react-helmet-async";
import { useMerchantSlug } from "@/utils/hooks/useMerchantSlug";
import useEventTracking from "@/utils/hooks/eventsHook";
import AdModal from "@/components/modals/AdModal";
import AnimatedGradientText from "@/components/ui/AnimatedGradientText";
import {
  setBranchId,
  setServiceFee,
  setTaxRate,
} from "@/store/features/cartSlice";
import {
  useGetCategoryFrontQuery,
  useLazyGetBranchFrontQuery,
} from "@/store/api/branchApi";
import { checkOperatingHours } from "@/utils/hooks/timeUtils";
import PageLoader from "@/components/ui/PageLoader";
import type { RootState } from "@/store";
import { getCurrentDayOperatingHours } from "@/utils/helpers/mergeOperatingHours";
import LocationIcon from "@/assets/img/LocationIcon";
import PhoneIcon from "@/assets/img/PhoneIcon";
import ChevronDownIcon from "@/assets/img/ChevronDownIcon";
import ClockIcon from "@/assets/img/ClockIcon";
import { Address } from "@/types/branch";
import RestaurantDetails from "@/components/store/RestaurantDetails";
import Menu from "@/components/store/Menu";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text";

// Lazy load components
const MemoizedCategoryNavigation = React.lazy(
  () => import("@/components/store/CategoryNavigation"),
);
const ProductList = React.lazy(() => import("@/components/store/ProductList"));

const StoreFront: React.FC = () => {
  const trackEvent = useEventTracking();
  const merchantSlug = useMerchantSlug();
  const [branchID, setBranchID] = useState<string>("");
  const dispatch = useDispatch();
  const [branch, setBranch] = useState<any>(null);
  const storeStatus = useSelector((state: RootState) => state.poll.storeStatus);
  const [visibleCategories, setVisibleCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isCategoryFixed, setIsCategoryFixed] = useState<boolean>(false);
  const promoTextRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [callModal, setCallModal] = useState(false);
  const [mapModal, setMapModal] = useState(false);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [address, setAddress] = useState<Address | null>(null);
  const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}`;
  const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
  const [menuResourcesLoaded, setMenuResourcesLoaded] = useState(false);

  const handleCall = () => {
    setCallModal(false); // Close the modal
    window.location.href = `tel:${branch.phone}`; // Initiate the call
  };
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Check for mobile screen size
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize); // Listen for window resize
    return () => window.removeEventListener("resize", handleResize); // Cleanup on unmount
  }, []);
  const [getBranchFront, { data: branchData, isLoading }] =
    useLazyGetBranchFrontQuery();
  const { data: categories, isLoading: isCategoriesLoading } =
    useGetCategoryFrontQuery(
      {
        branch: branchID,
        branchTimezone: branch?.timezone || "America/Chicago",
      },
      {
        skip: !branchID,
        refetchOnMountOrArgChange: true,
      },
    );

  const setPromoTextRef = useCallback((node: HTMLDivElement | null) => {
    if (promoTextRef.current && observer.current) {
      observer.current.unobserve(promoTextRef.current);
    }

    if (node && observer.current) {
      observer.current.observe(node);
    }

    promoTextRef.current = node;
  }, []);

  useEffect(() => {
    if (branch && storeStatus.isOpen === true) {
      // Explicitly check for true
      const lastVisitKey = `lastVisit_${branch.id}`;
      const lastVisit = localStorage.getItem(lastVisitKey);
      const currentTime = new Date().getTime();

      if (!lastVisit || currentTime - parseInt(lastVisit) > 3600000) {
        setShowModal(true);
        localStorage.setItem(lastVisitKey, currentTime.toString());
      }
    } else {
      // Ensure modal is closed if store is not open
      setShowModal(false);
    }
  }, [branch, storeStatus.isOpen]);

  useEffect(() => {
    if (branchData) {
      setBranch(branchData);
      setLatitude(branchData?.address?.latitude ?? 0);
      setLongitude(branchData?.address?.longitude ?? 0);
    }
  }, [branchData]);

  useEffect(() => {
    observer.current = new IntersectionObserver(
      ([entry]) => setIsCategoryFixed(!entry.isIntersecting),
      { threshold: 0 },
    );

    const currentPromoRef = promoTextRef.current;
    if (currentPromoRef) observer.current.observe(currentPromoRef);

    return () => {
      if (currentPromoRef && observer.current)
        observer.current.unobserve(currentPromoRef);
      observer.current?.disconnect();
    };
  }, []);

  // Fetch branch details and update open/close status
  const fetchBranchDetails = useCallback(async () => {
    try {
      const hostname = window.location.hostname;
      const urlToSend = `${hostname}${merchantSlug}`;
      const response = await getBranchFront(urlToSend).unwrap();

      if (response) {
        setBranch(response); // Set branch state
        setBranchID(response.id?.toString() ?? "");
        dispatch(setBranchId(response.id?.toString() ?? ""));
        dispatch(setServiceFee(parseFloat(response.service_fee ?? "0.00")));
        dispatch(setTaxRate(parseFloat(response.tax_rate ?? "0.00")));

        localStorage.setItem("selectedBranch", JSON.stringify(response));
      }
    } catch (error) {
      console.error("Error fetching branch details:", error);
    }
  }, [merchantSlug, getBranchFront, dispatch]);

  useEffect(() => {
    fetchBranchDetails();
  }, [fetchBranchDetails]);

  // Check category hours and filter visible categories
  useEffect(() => {
    if (categories && branch?.timezone) {
      const openCategories = categories
        .filter(
          (category) =>
            !category.category_hours?.length ||
            (checkOperatingHours(category.category_hours, branch.timezone) &&
              storeStatus.isOpen),
        )
        .map((category) => category.name);
      setVisibleCategories(openCategories);
    }
  }, [categories, storeStatus.isOpen, branch?.timezone]);

  // Select the first visible category
  useEffect(() => {
    if (visibleCategories.length && !selectedCategory) {
      setSelectedCategory(visibleCategories[0]);
    }
  }, [visibleCategories, selectedCategory]);

  const handleCategorySelect = useCallback((category: string) => {
    setSelectedCategory(category);
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  // Call the track event function when resources are loaded
  // useEffect(() => {
  //   // Only track when categories and branch data are loaded
  //   if (!isCategoriesLoading && !isLoading && branch && menuResourcesLoaded) {
  //     trackEvent();
  //   }
  // }, [isCategoriesLoading, isLoading, branch, trackEvent, menuResourcesLoaded]);

  return (
    <PageLoader isFetching={isCategoriesLoading || isLoading || !branch}>
      <Helmet>
        <title>
          {branch && branch.name ? branch.name : "RedBird Online Order"}
        </title>
        <link
          rel="icon"
          href={branch && branch.logo ? branch.logo : "/favicon.ico"}
        />
      </Helmet>
      <div className="flex flex-col min-h-screen items-center justify-center relative md:pt-20">
        <Header isSelectedBranchOpen={storeStatus.isOpen} />
        <AnimatedGradientText />
        {!storeStatus.isOpen && (
          <div className="w-full flex items-center justify-center px-4">
            <div className="group rounded-full border border-red-500 bg-neutral-100 text-base transition-all ease-in hover:cursor-pointer dark:border-white/5 dark:bg-neutral-900">
              <AnimatedShinyText
                shimmerWidth={150}
                className="inline-flex items-center justify-center px-6 py-2 text-lg md:text-xl font-medium text-gray-600 dark:text-gray-300 transition ease-out"
              >
                <span>
                  {storeStatus.reason === "operating-hours"
                    ? "✨ We're currently closed. Feel free to browse what we offer"
                    : "✨ We're currently closed. Please check back soon."}
                </span>
              </AnimatedShinyText>
            </div>
          </div>
        )}
        <div className="w-full max-w-7xl md:px-[3%] flex-grow">
          {branch && (
            <>
              <div>
                <div className="flex flex-col items-center mb-4 md:flex-row md:justify-between md:items-start">
                  <div className="w-full flex-1 mt-5 md:mt-0">
                    <img
                      src={branch.logo}
                      alt="Business"
                      className="w-full md:w-full h-52 md:h-96 md:rounded-l-xl object-cover"
                    />
                  </div>
                  <div className="md:w-sm hidden md:h-96 items-center md:rounded-r-xl md:block bg-blackOnyx">
                    <DesktopOperationalTimes
                      operatingHours={branch.operating_hours}
                    />
                  </div>
                </div>

                <div>
                  <div className="branch-info-modal">
                    <p className="text-center md:text-left text-2xl font-bold md:mb-5">
                      {branch.name}
                    </p>
                    <div className="flex flex-row items-center address text-xs font-normal mx-5 md:ml-0">
                      <LocationIcon />
                      <span
                        className="ml-1 cursor-pointer"
                        onClick={() => setMapModal(true)}
                      >
                        {branch.address.street}, {branch.address.city},{" "}
                        {branch.address.province_code}{" "}
                        {branch.address.postal_code}
                      </span>

                      {/* Modal */}
                      {mapModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-20 flex flex-col justify-end items-center z-50 pb-4 fade-in">
                          <div className="flex flex-col w-full items-center slide-up">
                            {/* Google Maps Button */}
                            <button
                              className="flex bg-white w-[90%] h-[3rem] px-4 py-2 rounded-lg items-center justify-center mb-2 text-center mx-auto"
                              onClick={() =>
                                window.open(googleMapsUrl, "_blank")
                              }
                            >
                              <span
                                className="font-normal text-lg"
                                style={{ color: "#00A3FF" }}
                              >
                                Open in Google Map
                              </span>
                            </button>

                            {/* Apple Maps Button */}
                            <button
                              className="flex bg-white w-[90%] h-[3rem] px-4 py-2 rounded-lg items-center justify-center mb-2 text-center mx-auto"
                              onClick={() =>
                                window.open(appleMapsUrl, "_blank")
                              }
                            >
                              <span
                                className="font-normal text-lg"
                                style={{ color: "#00A3FF" }}
                              >
                                Open in Apple Map
                              </span>
                            </button>

                            {/* Cancel Button */}
                            <button
                              className="flex bg-white w-[90%] h-[3rem] px-4 py-2 rounded-lg items-center justify-center text-center mx-auto"
                              onClick={() => setMapModal(false)}
                            >
                              <span
                                className="font-normal text-lg"
                                style={{ color: "#00A3FF" }}
                              >
                                Cancel
                              </span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row items-center address text-xs font-normal mx-5 md:my-2 md:ml-0">
                      <PhoneIcon />
                      <span
                        className="ml-1 cursor-pointer"
                        onClick={() => {
                          /*setCallModal(true) */
                          handleCall();
                        }}
                      >
                        {branch.ai_phone ? branch.ai_phone : branch.phone}
                      </span>

                      {/* Modal */}
                      {/* {callModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-20 flex flex-col justify-end items-center z-50 pb-4 fade-in">
                          <div className="flex flex-col w-full items-center slide-up">
                            <button
                              className="flex bg-white w-[90%] h-auto sm:h-[3rem] px-4 py-2 rounded-lg items-center justify-center my-2 text-center mx-auto"
                              onClick={handleCall}
                            >
                              <PhoneIcon className="w-4 h-4 mr-5" />
                              <span
                                className="font-normal text-lg"
                                style={{ color: "#00A3FF" }}
                              >
                                Call {branch.phone}
                              </span>
                            </button>
                            <button
                              className="flex bg-white w-[90%] h-auto sm:h-[3rem] px-4 py-2 rounded-lg items-center justify-center text-center mx-auto"
                              onClick={() => setCallModal(false)}
                            >
                              <span
                                className="font-normal text-lg"
                                style={{ color: "#00A3FF" }}
                              >
                                Cancel
                              </span>
                            </button>
                          </div>
                        </div>
                      )} */}
                    </div>
                    <div className="md:hidden">
                      <button
                        className="address flex items-center text-xs font-normal mx-5"
                        onClick={handleModalOpen}
                      >
                        <ClockIcon />
                        <span className="ml-2">
                          {getCurrentDayOperatingHours(branch.operating_hours)}
                        </span>
                        <span className="mx-3">
                          <ChevronDownIcon />
                        </span>
                      </button>
                    </div>
                    <RestaurantDetails
                      isModalOpen={isModalOpen}
                      handleModalClose={handleModalClose}
                      address={branch?.address ?? {}}
                      phoneNumber={branch?.phone ?? ""}
                      aiAgentPhoneNumber={branch?.ai_phone ?? ""}
                      branchName={branch?.name ?? ""}
                      operatingHours={branch.operating_hours}
                    />
                  </div>
                </div>
                {branch.promotion_text && (
                  <div
                    className="border border-graySilver mx-4 md:mx-0 text-xs p-4 rounded-lg"
                    ref={setPromoTextRef}
                  >
                    <p>{branch.promotion_text}</p>
                  </div>
                )}
              </div>
              <Menu
                branch={branch}
                setMenuResourcesLoaded={setMenuResourcesLoaded}
              />
            </>
          )}
        </div>
        {/* {!isMobile && (
          <Suspense fallback={<Spinner />}>
            <BranchMap
              address={branch?.address ?? {}}
              phoneNumber={branch?.phone ?? ""}
              branchName={branch?.name ?? ""}
            />
          </Suspense>
        )} */}
        <Suspense fallback={<Spinner />}>
          <Footer />
        </Suspense>
      </div>

      {branch && storeStatus.isOpen && (
        <AdModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          merchantId={branch.id.toString()}
          merchantName={branch.name}
          promotionText={branch.promotion_text}
        />
      )}
    </PageLoader>
  );
};

export default StoreFront;
